import React, { FC, useMemo, useCallback, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import _map from 'lodash/map';
import _cloneDeep from 'lodash/cloneDeep';
import { gqlRoleStatusEnum as ROLE_STATUS } from '@minecraft.graphql-types';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import { SelectOption } from '@minecraft.typings';
import ErrorMessage from '@minecraft.error-message';
import { FilterData } from '../../../shared/types';
import { QUERY_PROJECT_ROLES } from '../FilterBasicCollectionGraphQL';

type Props = FilterData & {
  withSearch?: boolean;
  dropdownPositionFixed?: boolean;
  projectId?: number;
  options?: SelectOption[];
  handleSubmitForm?: () => void;
};

const Role: FC<Props> = ({
  withSearch = true,
  projectId = 0,
  name,
  label,
  options,
  dropdownPositionFixed,
  handleSubmitForm,
}) => {
  const skipQuery = !projectId || options?.length > 0;

  const [getRoles, { data, error, loading }] = useLazyQuery(QUERY_PROJECT_ROLES, {
    variables: {
      projectId,
    },
  });

  const sortedRoleOptions = useMemo(() => {
    if (!data?.projectRoles) return [];

    const clonedProjectRoles = _cloneDeep(data?.projectRoles);
    const sortedRoles = clonedProjectRoles
      .filter((role) => ![ROLE_STATUS.ARCHIVED, ROLE_STATUS.ARCHIVE_IN_PROGRESS].includes(role.roleStatus.code))
      .sort((a, b): number => moment(b.created).unix() - moment(a.created).unix());

    return _map(sortedRoles, (role) => ({
      value: role.id,
      label: role.internalName || role.name,
    }));
  }, [data?.projectRoles]);

  const handleRolesFetch = useCallback((): void => {
    if (!data) {
      getRoles();
    }
  }, [data]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <DropdownWithChildren
      withFormik
      withSearch={withSearch}
      withSelectAll
      isMultipleSelect
      isLoading={loading}
      dropdownPositionFixed={dropdownPositionFixed}
      name={name}
      toggleLabel={label}
      options={options || sortedRoleOptions}
      onOptionsFetch={skipQuery ? undefined : handleRolesFetch}
      onOptionsChange={handleSubmitForm}
    />
  );
};

export default memo(Role);
